import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { USPBar } from "@/components/layout/USPBar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Brain, Globe, MessageCircle, ArrowRight, Phone, Mail, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import aiHeroBg from "@assets/IMG_3248_1773677070839.webp";

export default function AiSeo() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const features = [
    {
      icon: <Brain className="w-8 h-8 text-primary" />,
      title: "AI-Driven SEO Strategy",
      description: "Leverage artificial intelligence to analyse competitor strategies, identify content gaps, and optimise your website for next-generation search algorithms. Our AI tools help predict search trends and user behaviour patterns."
    },
    {
      icon: <Globe className="w-8 h-8 text-primary" />,
      title: "Geo AI Optimisation",
      description: "AI-powered local search strategy that dominates local search results, voice queries, and AI assistant responses. Whether customers search on Google, ask Siri, Alexa, or ChatGPT about services in your area, Geo AI ensures your business appears as the top answer."
    },
    {
      icon: <MessageCircle className="w-8 h-8 text-primary" />,
      title: "Answer Engine Optimisation (AEO)",
      description: "Optimise for AI-powered answer engines like ChatGPT, Gemini, and Perplexity. AEO focuses on featured snippets, knowledge graphs, and conversational queries to ensure your content appears in AI-generated responses."
    },
    {
      icon: <Zap className="w-8 h-8 text-primary" />,
      title: "Content Intelligence",
      description: "AI-powered content creation and optimisation that aligns with search intent. We analyse top-performing content and create comprehensive, structured data that resonates with both search engines and AI models."
    }
  ];

  return (
    <div className="min-h-screen bg-background font-sans">
      <Helmet>
        <title>AI SEO Services | The Cornwall SEO Co</title>
        <meta name="description" content="Future-proof your online presence with AI-powered SEO, Geo AI, and Answer Engine Optimisation strategies tailored for the modern search landscape." />
      </Helmet>
      <Navbar />

      {/* Hero Section */}
      <section 
        className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20 bg-black bg-cover bg-center"
        style={{ backgroundImage: `url(${aiHeroBg})` }}
      >
        <div className="absolute inset-0 bg-black/60 z-0"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl md:text-7xl font-heading font-black text-white mb-8 leading-none uppercase italic tracking-tighter">
              AI <br/>
              <span className="text-primary">SEO</span>
            </h1>
            <p className="text-xl text-white/90 mb-10 leading-relaxed font-medium">
              Future-proof your online presence with AI-powered SEO, Geo AI Optimisation, and Answer Engine Optimisation strategies tailored for the modern search landscape.
            </p>
            <a href="#contact-form" className="inline-block">
              <Button size="lg" className="bg-primary text-black hover:bg-white text-lg px-10 h-16 rounded-none font-black uppercase italic transition-all cursor-pointer">
                Contact Us <ArrowRight className="ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* USP Section */}
      <section className="py-10 bg-black border-y border-primary/20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-primary/20">
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">100% CORNWALL-BASED TEAM</div>
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">NO MINIMUM TERM CONTRACT</div>
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">COMPETITIVE HOURLY RATE</div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-3xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-6 uppercase italic tracking-tighter">
              The Future of <br/> <span className="text-primary">Search Marketing</span>
            </h2>
            <p className="text-black text-xl font-medium leading-relaxed mb-6">
              The search landscape is evolving rapidly. AI is reshaping how people find information, and traditional SEO alone isn't enough anymore. The Cornwall SEO Co combines classical SEO expertise with cutting-edge AI optimisation strategies to ensure your business thrives in 2025 and beyond.
            </p>
            <p className="text-black text-xl font-medium leading-relaxed mb-6">
              Our AI SEO approach goes beyond keywords and backlinks. We optimise for AI-powered search engines, voice search, geographic intent, and answer engines that are fundamentally changing how users discover businesses like yours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full rounded-none border-2 border-black p-6">
                  <CardHeader className="p-0 mb-4 flex flex-row items-start gap-4">
                    {feature.icon}
                    <CardTitle className="font-heading font-black text-xl md:text-xl text-black uppercase italic flex-1">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <p className="text-black font-medium leading-relaxed text-lg md:text-sm">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Geo AI Optimisation Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-4xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-8 uppercase italic tracking-tighter">
              Geo AI <br/> <span className="text-primary">Optimisation</span>
            </h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-heading font-black text-black mb-4 uppercase italic">AI-Powered Local Search</h3>
                <p className="text-black text-lg font-medium leading-relaxed mb-6">
                  Geo AI Optimisation is the next evolution of local search strategy. It combines traditional geographic targeting with AI intelligence to dominate local search results, voice queries, and location-based AI assistant responses. Whether customers are searching on Google, asking Siri, Alexa, or ChatGPT about services in your area, Geo AI ensures your business appears as the top answer.
                </p>
              </div>

              <div className="bg-black text-white p-8 border-l-8 border-primary">
                <h4 className="font-heading font-black uppercase italic text-xl mb-4">Geo AI Optimisation Strategies:</h4>
                <ul className="space-y-3 font-medium">
                  <li>✓ AI-powered local keyword mapping and intent analysis</li>
                  <li>✓ Voice search optimisation for "near me" and location queries</li>
                  <li>✓ Multi-location AI content strategy</li>
                  <li>✓ AI assistant training (ChatGPT, Gemini, Perplexity local queries)</li>
                  <li>✓ Geographic schema markup and structured local data</li>
                  <li>✓ Local knowledge graph integration with AI engines</li>
                  <li>✓ Reputation management across AI platforms</li>
                  <li>✓ Location-specific conversational content for AI</li>
                  <li>✓ Google Business Profile AI optimisation</li>
                  <li>✓ Local authority building for AI citations</li>
                </ul>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card className="rounded-none border-2 border-black p-6">
                  <CardTitle className="font-heading font-black text-black uppercase italic mb-4 text-lg">Why Geo AI Matters</CardTitle>
                  <p className="text-black font-medium leading-relaxed text-sm">
                    AI assistants are becoming the primary way people search for local services. Your business needs to be visible not just in Google Maps, but in AI-generated recommendations and responses for location-based queries.
                  </p>
                </Card>

                <Card className="rounded-none border-2 border-black p-6">
                  <CardTitle className="font-heading font-black text-black uppercase italic mb-4 text-lg">Geo AI vs Traditional Local SEO</CardTitle>
                  <p className="text-black font-medium leading-relaxed text-sm">
                    Traditional local SEO focuses on Google Maps and local pack rankings. Geo AI extends beyond that to voice assistants, conversational AI, and multi-channel local presence optimisation.
                  </p>
                </Card>
              </div>

              <p className="text-black text-lg font-medium leading-relaxed">
                With voice searches and AI assistant queries growing exponentially, businesses that optimise for Geo AI will dominate local markets. We ensure customers searching locally—across all platforms and devices—find you first, not your competitors.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* AEO Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-4xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-8 uppercase italic tracking-tighter">
              Answer Engine <br/> <span className="text-primary">Optimisation (AEO)</span>
            </h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-heading font-black text-black mb-4 uppercase italic">The New Frontier of Search</h3>
                <p className="text-black text-lg font-medium leading-relaxed mb-6">
                  Answer Engine Optimisation (AEO) is the evolution of SEO for AI-powered search platforms. As ChatGPT, Google Gemini, Perplexity, and other AI engines become primary search tools, your content needs optimisation for these new platforms.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card className="rounded-none border-2 border-black p-6">
                  <CardTitle className="font-heading font-black text-black uppercase italic mb-4 text-lg">Why AEO Matters</CardTitle>
                  <p className="text-black font-medium leading-relaxed text-sm">
                    AI search engines don't rely solely on click-through rates. They analyse content quality, credibility, comprehensiveness, and structured data. Optimising for AEO ensures your brand appears in AI-generated responses.
                  </p>
                </Card>

                <Card className="rounded-none border-2 border-black p-6">
                  <CardTitle className="font-heading font-black text-black uppercase italic mb-4 text-lg">AEO vs Traditional SEO</CardTitle>
                  <p className="text-black font-medium leading-relaxed text-sm">
                    While traditional SEO focuses on ranking for keywords, AEO focuses on being the source AI engines cite. It's about being authoritative, comprehensive, and conversational in your content approach.
                  </p>
                </Card>
              </div>

              <div className="bg-black text-white p-8 border-l-8 border-primary">
                <h4 className="font-heading font-black uppercase italic text-xl mb-4">AEO Optimisation Pillars:</h4>
                <ul className="space-y-3 font-medium">
                  <li>✓ E-E-A-T Signals (Experience, Expertise, Authoritativeness, Trustworthiness)</li>
                  <li>✓ Featured Snippet Optimisation</li>
                  <li>✓ Knowledge Graph Integration</li>
                  <li>✓ Conversational Content Strategy</li>
                  <li>✓ Structured Data & Schema Markup</li>
                  <li>✓ Long-form, Comprehensive Content</li>
                  <li>✓ Citation and Credibility Building</li>
                  <li>✓ FAQ Schema and Direct Answer Formats</li>
                </ul>
              </div>

              <p className="text-black text-lg font-medium leading-relaxed">
                We craft content that answers user questions comprehensively—exactly what AI engines look for. By focusing on the source content quality and authority, your business becomes a trusted citation in AI-powered responses.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-3xl">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-8 uppercase italic tracking-tighter">
              Why The <br/> <span className="text-primary">Cornwall SEO Co</span>
            </h2>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-primary flex items-center justify-center shrink-0 font-black text-black">✓</div>
                <div>
                  <h4 className="font-heading font-black text-black uppercase italic text-lg mb-2">Future-Ready Strategies</h4>
                  <p className="text-black font-medium">We don't just optimise for today's search engines—we prepare your business for the AI-driven search landscape of tomorrow.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-primary flex items-center justify-center shrink-0 font-black text-black">✓</div>
                <div>
                  <h4 className="font-heading font-black text-black uppercase italic text-lg mb-2">Local + Global Expertise</h4>
                  <p className="text-black font-medium">Rooted in St Austell with UK-wide reach, we understand local markets while applying global SEO best practices.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-primary flex items-center justify-center shrink-0 font-black text-black">✓</div>
                <div>
                  <h4 className="font-heading font-black text-black uppercase italic text-lg mb-2">Data-Driven Approach</h4>
                  <p className="text-black font-medium">Our AI tools analyse real search patterns, competitor strategies, and emerging trends to inform every decision.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-primary flex items-center justify-center shrink-0 font-black text-black">✓</div>
                <div>
                  <h4 className="font-heading font-black text-black uppercase italic text-lg mb-2">Transparent Reporting</h4>
                  <p className="text-black font-medium">Regular reports showing rankings improvements, traffic increases, and ROI from your AI SEO investment.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact-form" className="py-12 md:py-24 bg-primary">
        <div className="container mx-auto px-3 md:px-6">
          <div className="bg-black p-4 md:p-20 shadow-[20px_20px_0px_0px_rgba(255,255,255,0.3)] overflow-x-hidden">
            <div className="grid md:grid-cols-2 gap-4 md:gap-16">
              <div>
                <h3 className="text-3xl md:text-6xl font-heading font-black text-primary mb-4 md:mb-6 uppercase italic tracking-tighter">Ready to Lead <br/> in AI-Driven Search?</h3>
                <p className="text-white/80 text-base md:text-xl mb-6 md:mb-12 font-medium">
                  Let's build an AI SEO strategy that positions your business as an authority and gets you found by customers using next-generation search tools.
                </p>
                
                <div className="space-y-4 md:space-y-8">
                   <div className="flex items-center gap-3 md:gap-6">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <Phone size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <span className="text-white font-black uppercase italic text-sm md:text-xl break-words">01726 74161</span>
                   </div>
                   <div className="flex items-center gap-3 md:gap-6">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <Mail size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <a href="mailto:hello@thecornwallseoco.co.uk" className="text-white font-black uppercase italic text-xs md:text-xl hover:text-primary transition-colors break-words">hello@thecornwallseoco.co.uk</a>
                   </div>
                   <a
                     href="https://wa.me/447792646061"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="flex items-center gap-3 md:gap-6 hover:text-primary transition-colors"
                   >
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <MessageCircle size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <span className="text-white font-black uppercase italic text-sm md:text-xl hover:text-primary transition-colors">WhatsApp</span>
                   </a>
                </div>
              </div>
              
              <form action="/contact.php" method="POST" className="space-y-3 md:space-y-4">
                <Input name="name" required placeholder="NAME" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Input name="email" required type="email" placeholder="EMAIL" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Input name="website" placeholder="WEBSITE" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Textarea name="message" required placeholder="HOW CAN WE HELP YOU?" className="bg-white border-none rounded-none h-24 md:h-32 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Button type="submit" className="w-full bg-primary text-black hover:bg-white h-14 md:h-16 font-black uppercase italic text-lg md:text-xl rounded-none transition-all cursor-pointer">
                  GO
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
